import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { EditIcon3 } from 'assets';
import { toTitleCase } from 'utils/titleCase';
import { JobCriteriaData } from '../types';
import isEqual from 'lodash/isEqual';
import { BasicExpectationEditModal, BasicExpectationFormData, buildBasicExpectationPatch, hasBasicExpectationChanges } from 'components';
import { BasicExpectationResponse, BasicExpectationsEditPayload, useBasicExpectationsEdit, useFetchBasicExpectations } from 'hooks/criteria-modal-hooks';
import { formatWorkMode, normalizeWorkMode, shouldCollectJobLocation } from 'utils/workMode';

export const getLabelFromValue = (value: string | undefined): string => {
  const options = [
    { label: 'Full-time', value: 'fulltime' },
    { label: 'Part-time', value: 'parttime' },
    { label: 'Contract', value: 'contract' },
    { label: 'Internship', value: 'internship' },
  ];
  const match = options.find(option => option.value === value);
  return match ? match.label : (value ?? '');
};

export const formatYearsRange = (input: string | undefined): string => {
  if (typeof input !== 'string') return String(input ?? '');
  const lower = input.trim().toLowerCase();

  // Open-ended values like "OVER_10", "10_plus" or "10+" should read as "10+ years".
  // (Checked before the normalize step below, whose char class strips the "o" in "over".)
  if (/over|plus|\+/.test(lower)) {
    const num = lower.match(/\d+/);
    if (num) return `${Number(num[0])}+ years`;
  }

  const normalized = lower.replace(/[_–—to]+/g, '-').replace(/\s+/g, '');
  const [start, end] = normalized.split('-');
  if (!isNaN(Number(start)) && !isNaN(Number(end))) {
    return `${Number(start)} - ${Number(end)} years`;
  }
  if (!isNaN(Number(input))) {
    return `${Number(input)} years`;
  }
  return input;
};

interface BasicExpectationSectionProps {
  assessmentId: string | null;
  data: JobCriteriaData['basicExpectation'];
  onEdit: () => void;
  onSave: (updatedData: BasicExpectationFormData) => void;
  disabled?: boolean;
}

const initialData = {
  employmentType: '',
  experienceLevel: '',
  yearsOfExperience: '0',
  workMode: '',
  jobLocation: '',
  workEnvironment: '',
  minSalary: '',
  maxSalary: '',
  salaryCurrency: '',
  salaryPeriod: '',
  isEmploymentType: true,
  isExperienceLevel: true,
  isYearsOfExperience: true,
  isWorkMode: true,
  isWorkEnvironment: true,
  isSalaryRange: true,
  isCV: true,
  isPortfolio: true,
};

/**
 * Shape the GET response into the form's own shape. The server is the source of truth for this
 * section: the `data` prop only holds what the chat message was parsed into at render time, so it
 * goes stale the moment an edit is saved and would show pre-edit values again on the next render.
 */
const mapResponseToFormData = (
  be: BasicExpectationResponse['data'] | undefined,
): BasicExpectationFormData | null => {
  if (!be) return null;

  return {
    employmentType: be.employment_type ?? '',
    experienceLevel: be.experience_level ?? '',
    yearsOfExperience: be.years_of_experience?.toString() ?? '0',
    workMode: normalizeWorkMode(be.work_mode),
    jobLocation: be.address ?? '',
    workEnvironment: be.experience_environment ?? '',
    minSalary: be.min_salary_range_amount?.toString() ?? '',
    maxSalary: be.max_salary_range_amount?.toString() ?? '',
    salaryCurrency: be.salary_range_currency ?? '',
    salaryPeriod: be.salary_range_term ?? '',
    isEmploymentType: be.require_employment_type ?? true,
    isExperienceLevel: be.require_experience_level ?? true,
    isYearsOfExperience: be.require_years_of_experience ?? true,
    isWorkMode: be.require_work_mode ?? true,
    isWorkEnvironment: be.require_work_environment ?? true,
    isSalaryRange: be.require_salary_range ?? true,
    displaySalary: be.display_salary ?? false,
    isCV: be.require_cv_upload ?? true,
    isPortfolio: be.require_portfolio_upload ?? true,
  };
};

const FieldToggle: React.FC<{ value: boolean; onToggle: () => void }> = ({ value, onToggle }) => (
  <label className="flex items-center cursor-pointer flex-shrink-0">
    <input type="checkbox" className="hidden" checked={value} onChange={onToggle} />
    <span className={`relative inline-block w-10 h-6 rounded-full transition-all duration-300 flex-shrink-0 ${value ? 'bg-[#012169]' : 'bg-[#e5e7eb]'}`}>
      <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-all duration-300 ${value ? 'translate-x-4' : 'translate-x-0'}`} />
    </span>
  </label>
);

type ToggleField = 'displaySalary' | 'requireEmploymentType' | 'requireExperienceLevel' | 'requireYearsOfExperience' | 'requireWorkEnvironment';

// Maps an inline toggle to the single PATCH field it updates, so a toggle sends only that value.
const toggleFieldToPayloadKey: Record<ToggleField, keyof BasicExpectationsEditPayload> = {
  displaySalary: 'display_salary',
  requireEmploymentType: 'require_employment_type',
  requireExperienceLevel: 'require_experience_level',
  requireYearsOfExperience: 'require_years_of_experience',
  requireWorkEnvironment: 'require_work_environment',
};

const BasicExpectationSection: React.FC<BasicExpectationSectionProps> = ({
  assessmentId,
  data,
  onEdit,
  onSave,
  disabled,
}) => {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  // Saved edits land on the server and the PATCH invalidates this query, so reading from it is
  // what keeps the section showing the persisted values rather than the chat's original parse.
  const { basicExpectation } = useFetchBasicExpectations(assessmentId);
  const serverData = useMemo(() => mapResponseToFormData(basicExpectation?.data), [basicExpectation?.data]);
  const effectiveData = serverData ?? data;

  const [displaySalary, setDisplaySalary] = useState(effectiveData?.displaySalary ?? false);
  const [requireEmploymentType, setRequireEmploymentType] = useState(effectiveData?.isEmploymentType ?? true);
  const [requireExperienceLevel, setRequireExperienceLevel] = useState(effectiveData?.isExperienceLevel ?? true);
  const [requireYearsOfExperience, setRequireYearsOfExperience] = useState(effectiveData?.isYearsOfExperience ?? true);
  const [requireWorkEnvironment, setRequireWorkEnvironment] = useState(effectiveData?.isWorkEnvironment ?? true);

  useEffect(() => {
    setDisplaySalary(effectiveData?.displaySalary ?? false);
    setRequireEmploymentType(effectiveData?.isEmploymentType ?? true);
    setRequireExperienceLevel(effectiveData?.isExperienceLevel ?? true);
    setRequireYearsOfExperience(effectiveData?.isYearsOfExperience ?? true);
    setRequireWorkEnvironment(effectiveData?.isWorkEnvironment ?? true);
  }, [effectiveData?.displaySalary, effectiveData?.isEmploymentType, effectiveData?.isExperienceLevel, effectiveData?.isYearsOfExperience, effectiveData?.isWorkEnvironment]);

  const {
    submitBasicExpectations,
    isSubmitting,
  } = useBasicExpectationsEdit(assessmentId);

  const onSubmit = useCallback((formData: BasicExpectationFormData) => {
    if (!assessmentId || isSubmitting || !effectiveData) return;

    // Only send the fields the user actually changed (PATCH leaves the rest untouched).
    const payload = buildBasicExpectationPatch(formData, effectiveData, assessmentId);

    // Nothing changed — just close the modal, no redundant request.
    if (!hasBasicExpectationChanges(payload)) {
      setIsEditModalOpen(false);
      return;
    }

    submitBasicExpectations(payload, {
      onSuccess: () => {
        onSave(formData);
        setIsEditModalOpen(false);
      },
      onError: () => {
        setIsEditModalOpen(false);
      },
    });
  }, [assessmentId, isSubmitting, effectiveData, submitBasicExpectations, onSave]);

  const toggleSetters: Record<ToggleField, React.Dispatch<React.SetStateAction<boolean>>> = {
    displaySalary: setDisplaySalary,
    requireEmploymentType: setRequireEmploymentType,
    requireExperienceLevel: setRequireExperienceLevel,
    requireYearsOfExperience: setRequireYearsOfExperience,
    requireWorkEnvironment: setRequireWorkEnvironment,
  };

  const toggleValues: Record<ToggleField, boolean> = {
    displaySalary,
    requireEmploymentType,
    requireExperienceLevel,
    requireYearsOfExperience,
    requireWorkEnvironment,
  };

  const handleToggleField = useCallback((field: ToggleField) => {
    if (!assessmentId || isSubmitting || !effectiveData) return;
    const current = toggleValues[field];
    const next = !current;
    toggleSetters[field](next);

    // Toggling one switch updates that single boolean only — don't resend the whole form.
    const payload: BasicExpectationsEditPayload = {
      assessment_id: assessmentId,
      [toggleFieldToPayloadKey[field]]: next,
    };

    submitBasicExpectations(payload, {
      onError: () => {
        toggleSetters[field](current);
      },
    });
  }, [assessmentId, isSubmitting, effectiveData, toggleValues, toggleSetters, submitBasicExpectations]);

  if (isEqual(effectiveData, initialData)) return null;
  if (!effectiveData) return null;

  const showEmploymentType = effectiveData.isEmploymentType ?? true;
  const showExperienceLevel = effectiveData.isExperienceLevel ?? true;
  const showYearsOfExperience = effectiveData.isYearsOfExperience ?? true;
  const showWorkMode = effectiveData.isWorkMode ?? true;
  const showWorkEnvironment = effectiveData.isWorkEnvironment ?? true;
  const showSalaryRange = effectiveData.isSalaryRange ?? true;

  const salaryRange = effectiveData.minSalary && effectiveData.maxSalary
    ? `${Number(effectiveData.minSalary).toLocaleString()} – ${Number(effectiveData.maxSalary).toLocaleString()}`
    : null;

  return (
    <section className="border-t border-[#dfe1e6] pt-6 mb-8 relative">
      <div className="flex justify-between items-center mb-5">
        <h2 className="text-xl sm:text-2xl font-medium sm:font-semibold m-0">Basic Expectation</h2>
        {/* {!disabled && ( */}
        <div className="flex gap-3.5 items-center">
          <span className="cursor-pointer">
            <EditIcon3 onClick={() => { onEdit(); setIsEditModalOpen(true); }} />
          </span>
        </div>
        {/* )} */}
      </div>

      <div className="flex flex-col gap-3 text-sm">
        {showEmploymentType && (
          <div className="flex gap-2 items-center justify-between">
            <div>
              <span className="text-[#344054] font-medium whitespace-nowrap mr-2">Employment type:</span>
              <span className="font-semibold text-[#1D2939] capitalize">{getLabelFromValue(effectiveData?.employmentType)}</span>
            </div>
          </div>
        )}

        {showExperienceLevel && (
          <div className="flex gap-2 items-center justify-between">
            <div>
              <span className="text-[#344054] font-medium whitespace-nowrap mr-2">Experience level:</span>
              <span className="font-semibold text-[#1D2939] capitalize">{effectiveData?.experienceLevel}</span>
            </div>
          </div>
        )}

        {showYearsOfExperience && (
          <div className="flex gap-2 items-center justify-between">
            <div>
              <span className="text-[#344054] font-medium whitespace-nowrap mr-2">Years of experience:</span>
              <span className="font-semibold text-[#1D2939]">{Number(effectiveData?.yearsOfExperience) !== 0 && formatYearsRange(effectiveData?.yearsOfExperience)}</span>
            </div>
          </div>
        )}

        {showWorkMode && (
          <div className="flex gap-2 items-center justify-between">
            <div>
              <span className="text-[#344054] font-medium whitespace-nowrap mr-2">Work mode:</span>
              <span className="font-semibold text-[#1D2939]">
                {formatWorkMode(effectiveData.workMode) || '--/--'}
              </span>
            </div>
          </div>
        )}

        {showWorkMode && shouldCollectJobLocation(effectiveData.workMode) && (
          <div className="flex gap-2 items-center justify-between">
            <div>
              <span className="text-[#344054] font-medium whitespace-nowrap mr-2">Job location:</span>
              <span className="font-semibold text-[#1D2939]">{effectiveData.jobLocation || '--/--'}</span>
            </div>
          </div>
        )}

        {showWorkEnvironment && (
          <div className="flex gap-2 items-center justify-between">
            <div>
              <span className="text-[#344054] font-medium whitespace-nowrap mr-2">Work environment preference:</span>
              <span className="font-semibold text-[#1D2939] capitalize">{effectiveData?.workEnvironment ? toTitleCase(effectiveData.workEnvironment) : '--/--'}</span>
            </div>
          </div>
        )}


        <div className="flex gap-2 items-center justify-between">
          {showSalaryRange && (
            <div>
              <span className="text-[#344054] font-medium whitespace-nowrap mr-2">Salary range:</span>
              <span className="font-semibold text-[#1D2939]">
                {salaryRange ? (
                  <>
                    {`${salaryRange}  ${effectiveData.salaryCurrency || ''}`}
                    <span className="hidden lg:inline">{` (${effectiveData.salaryPeriod || ''})`}</span>
                  </>
                ) : '--/--'}
              </span>
            </div>)}
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="text-sm font-medium text-[#374151] whitespace-nowrap">
              Display<span className="hidden lg:inline"> Salary</span>
            </span>
            <FieldToggle value={displaySalary} onToggle={() => handleToggleField('displaySalary')} />
          </div>
        </div>

      </div>

      <BasicExpectationEditModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        currentData={effectiveData}
        onSave={onSubmit}
        isSubmitting={isSubmitting}
      />
    </section>
  );
};

export default BasicExpectationSection;
